import type { LiveProcessValue } from 'features/knowledgeDocumentViewer/hooks/useLiveProcessDataValues'

const EXACT_SCORE = 100
const LOOP_SCORE = 80
const CONTAINS_SCORE = 60

const ISA_MODIFIERS = /[EITC]/g

const compact = (text: string): string =>
  text.replace(/[-_\s.]/g, '').toUpperCase()

/**
 * Drop the historian namespace a datapoint was recorded under, so
 * `BOP1.BOP2_01-VI-BFW140J` compares as `01-VI-BFW140J`.
 */
function stripHistorianPrefix(tag: string): string {
  return tag.replace(/^(?:[A-Za-z]{2,}\d*[._])+/, '')
}

/**
 * Identity of the measurement loop a tag belongs to: the ISA function code
 * keeps its first letter and loses its element/indicator/transmitter/controller
 * modifiers, so `01-TE-CWS108`, `01-TI-CWS108` and `01-TT-CWS108` share a key.
 */
function loopKey(tag: string): string {
  const segments = stripHistorianPrefix(tag).toUpperCase().split(/[-_.]/)
  return segments
    .map((segment, index) =>
      index === 1 && segment.length > 1
        ? segment.charAt(0) + segment.slice(1).replace(ISA_MODIFIERS, '')
        : segment
    )
    .join('')
}

/** How well a datapoint's own tag answers to the label printed on the drawing. */
function tagMatchScore(label: string, piTag: string): number {
  const tag = compact(stripHistorianPrefix(piTag))
  const target = compact(label)

  if (tag === target) return EXACT_SCORE
  if (loopKey(piTag) === loopKey(label)) return LOOP_SCORE
  if (tag.startsWith(target) || target.startsWith(tag)) return CONTAINS_SCORE
  return 0
}

/**
 * Order datapoints by how well each answers to `label`, best first: the tag
 * match itself, then the canonical tag ahead of a historian-namespaced copy of
 * it, then the order the links came in. Ranking the same way before and after
 * fetching keeps the datapoint we chose to read and the reading we choose to
 * show in agreement.
 */
export function rankByTagMatch<T extends { piTag: string }>(
  label: string,
  datapoints: readonly T[]
): T[] {
  return [...datapoints]
    .map((datapoint, order) => ({ datapoint, order }))
    .sort(
      (a, b) =>
        tagMatchScore(label, b.datapoint.piTag) -
          tagMatchScore(label, a.datapoint.piTag) ||
        Number(isCanonical(b.datapoint.piTag)) -
          Number(isCanonical(a.datapoint.piTag)) ||
        a.order - b.order
    )
    .map(({ datapoint }) => datapoint)
}

const isCanonical = (piTag: string): boolean =>
  piTag === stripHistorianPrefix(piTag)

/**
 * The single reading to show for a tag that resolves to several datapoints.
 * How well a datapoint answers to the label decides it, so a stale reading
 * from the right tag still beats a fresh one from a looser match; freshness
 * only separates datapoints the label answers to equally well.
 */
export function pickBestLiveValue(
  label: string,
  readings: readonly LiveProcessValue[]
): LiveProcessValue | undefined {
  const ranked = rankByTagMatch(label, readings)
  const best = ranked[0]
  if (!best) return undefined

  const bestScore = tagMatchScore(label, best.piTag)
  return (
    ranked.find(
      (reading) =>
        !reading.isStale && tagMatchScore(label, reading.piTag) === bestScore
    ) ?? best
  )
}
